import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Decimal } from 'decimal.js';
import * as moment from 'moment';
import { PcnModel } from '../../viewmodels/pcn.model';
import { PcnBlockService } from '../../services/pcn-block.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DocumentsService } from '../../services/documents.service';
import { QuestionService } from '../../services/question.service';
import { Global } from '../../../../app.global';
import { GlobalObservables } from '../../../../app.global.observer';
import { LetterService } from '../../services/letter.service';
import { AlertsService } from '../../services/alerts.service';
import { debounceTime, SubscriptionLike as ISubscription, Subject } from 'rxjs';
import { Constants } from '../../helper/constants';
import { AuthenticationService } from '../../../../authentication/services/authentication.service';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/en/currencies';
import { SettingsService } from '../../../../authentication/services/settings.service';
import { StorageService } from '../../services/storage.service';
import { PcnFieldConfirmation } from '../../viewmodels/pcn-field-confirmation.model';
import { ChargeViewModel } from '../../viewmodels/chargeViewModel';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'pcn-block',
  templateUrl: './pcn-block.component.html',
  styleUrls: ['./pcn-block.component.scss'],
})
export class PcnBlockComponent implements OnInit, OnDestroy {
  @ViewChild('pcnForm') pcnForm: NgForm;
  @ViewChild('tPcn') tooltipPcn: NgbTooltip;
  @ViewChild('tContravention', { static: true })
  tooltipContravention: NgbTooltip;
  @ViewChild('contraventionField') contraventionField: ElementRef;

  pcnPanelLocked: boolean;
  constants = Constants;
  pcnDetails: PcnModel;
  pcnNumberClass: string;
  contraventionClass: string;
  interactPcn: boolean;
  pcnNumberValidation: boolean;
  isActionPcn: boolean;
  dateIssued: Date = new Date();
  paymentDate: Date = new Date();
  letterDate: Date = new Date();
  submitButtonValue = 'Continue';
  cctvContraventions = [31, 32, 33, 34, 37, 38, 50, 51, 52, 53, 54, 13, 17, 29, 39, 58, 59, 67, 75];
  pcnPrefix = 'BL'; // default value
  savingStatus = false;
  waitingFineAmount = false;
  isUnhandledError = false;
  isPcnPrefixError = false;
  isServerPcnError = false;
  serverPcnErrorMessage = '';
  isStagesLoading = false;
  isChargeNowEnabled = true;
  isActionChanging = false;
  isAdminAccount = false;
  isInterfacedPcn = false;
  isProcessingInterface = false;
  isApplyingPayload = false;
  pendingPayloadStage: string | null = null;
  // Populated (and shown via pcn-confirmation-modal, driven from welcome-layout.component.html)
  // when processInterfacedPcn refuses to save because one or more fields - e.g. Fine Now - could
  // not be automatically determined.
  pendingConfirmations: PcnFieldConfirmation[] = [];
  // fineNowChangedMessage = false;
  actionsLayout = 'normal';
  public currencyFormatOptions: NumberFormatOptions = {
    style: 'currency',
    currency: 'GBP',
    currencyDisplay: 'symbol',
  };
  councilLogo = '';
  disablePcnBlock = false;

  isContraventionValid = false;
  isContraventionEnabled = false;

  formChanged = false; // Tracks if any field has changed
  initialState: Partial<PcnModel>; // Stores initial field values for comparison
  hasPendingChanges = false; // Tracks if form has unsaved changes requiring Update button
  // Stores original pin state before force-pinning

  contraventionInput$ = new Subject<string>();
  pdfUrl = '';
  payloadSubmitTimer?: number;

  /* Temp Values for Two-Way binding */
  tempContravention = '';
  tempStageId = 0;
  tempActionId = 0;
  tempSourceId = 0;
  tempPaidAmount = new Decimal(0);
  tempChargeId = 0;

  isExistingPcn = false;

  /* Subscriptions */
  private panelStateBeforeChanges: boolean | null = null;
  private onPcnResetRequestEvent: ISubscription;
  private onQuestionsChangingEvent: ISubscription;
  private onQuestionsChangedEvent: ISubscription;
  private onQuestionsFailedEvent: ISubscription;
  private onLetterChangingEvent: ISubscription;
  private onLetterChangedEvent: ISubscription;

  constructor(
    private pcnBlockService: PcnBlockService,
    private router: Router,
    public global: Global,
    public globalObs: GlobalObservables,
    public questionServ: QuestionService,
    public docService: DocumentsService,
    public letterService: LetterService,
    public alertService: AlertsService,
    private authService: AuthenticationService,
    private settingsService: SettingsService,
    private storageService: StorageService
  ) {
    if (this.router.url !== '/pcn-details') {
      this.submitButtonValue = 'Update';
    }
  }

  /* Getters and Setters */
  get paidAmountValue(): number {
    return this.tempPaidAmount.toNumber(); // Used only by the UI
  }
  get pcnLocked(): boolean {
    return this.storageService.isPcnInputLocked();
  }

  set paidAmountValue(val: number | null) {
    try {
      this.tempPaidAmount = new Decimal(val ?? 0);
    } catch {
      this.tempPaidAmount = new Decimal(0);
    }
  }
  set pcnLocked(value: boolean) {
    this.storageService.lockPcnInput(value);
  }

  ngOnInit() {
    // Setup debounce for contravention input. This will prevent the contravention validation from firing too often
    this.contraventionInput$
      .pipe(debounceTime(500)) // adjust delay (ms) as needed
      .subscribe((contravention) => {
        this.onChangeContraventionDebounced(contravention);
      });

    this.onQuestionsChangingEvent = this.globalObs.onQuestionsChanging.subscribe(() =>
      this.onQuestionsChanging()
    );
    this.onQuestionsChangedEvent = this.globalObs.onQuestionsChanged.subscribe(() =>
      this.onQuestionsChanged()
    );
    this.onQuestionsFailedEvent = this.globalObs.onQuestionsFailed.subscribe(() =>
      this.onQuestionsFailed()
    );
    this.onLetterChangingEvent = this.globalObs.onLetterChanging.subscribe(() =>
      this.onLetterChanging()
    );
    this.onLetterChangedEvent = this.globalObs.onLetterChanged.subscribe(() =>
      this.onLetterChanged()
    );
    this.onPcnResetRequestEvent = this.globalObs.onPcnResetRequest.subscribe(() =>
      this.onPcnResetRequest()
    );

    // check if the current user is an admin and set the flag
    this.setIsAdminAccount();
    // sets the pcn to default values
    this.setPcnDefaultValue();

    this.setPcnStorageValue();

    // get the pcn prefix for logged in client
    this.getPcnPrefix();

    this.tokenDataCheck();

    // this.fineNowChanged();

    const locked = this.pcnBlockService.checkPcnPanelLocked();
    this.global.setPcnPanelLocked(locked);
    this.settingsService.getApplicationSetting('Council.Logo').subscribe(
      (result) => {
        this.councilLogo = result;
      },
      (err) => {
        this.councilLogo = `${this.constants.assetsPath}/img/barbour-logic-logo.png`;
      }
    );

    // check the pcn details for a contravention and validate if it is valid
    if (
      this.pcnDetails !== null &&
      this.pcnDetails.pcn !== null &&
      this.pcnDetails.pcn.trim().length > 0
    ) {
      this.isContraventionEnabled = true;

      this.pcnBlockService.validateContravention(this.pcnDetails.contravention).subscribe({
        next: (result) => {
          // console.log("contraValResult: ", result);
          this.tempContravention = this.pcnDetails.contravention;
          this.tempStageId = this.pcnDetails.selectedStageId;
          this.tempActionId = this.pcnDetails.selectedActionId;
          this.tempSourceId = this.pcnDetails.selectedSourceId;
          this.tempPaidAmount = new Decimal(this.pcnDetails.paidAmount);
          this.tempChargeId = this.pcnDetails.selectedChargeId;

          if (result.success) {
            this.isContraventionValid = true;
            this.contraventionClass = 'success';
            this.pcnDetails.contraventionSuffix = result.suffix;
            this.isChargeNowEnabled = true;
          } else {
            this.isChargeNowEnabled = false;
            this.contraventionClass = 'error';
            this.isContraventionValid = false;
            this.tooltipContravention.open();
          }
        },
        error: (error) => {
          this.isContraventionValid = false;
          this.isChargeNowEnabled = false;
        },
      });
    }
  }

  togglePcnPanelLocked() {
    // Prevent unpinning if there are unsaved changes
    if (this.global.isPcnPanelLocked && this.hasPendingChanges) {
      return; // Don't allow unpinning while changes are pending
    }

    const newLockState = !this.storageService.isPcnPanelLocked();
    this.storageService.lockPcnPanel(newLockState);
    this.global.setPcnPanelLocked(newLockState);
  }

  // Called by pcn-confirmation-modal (via welcome-layout.component.html) once the user has picked
  // a value for every pending confirmation. Only FineNow is handled today - a future second
  // confirmable field would need its own branch here alongside this one, keyed by fieldName the
  // same way.
  onConfirmationsResolved(selections: Record<string, ChargeViewModel>): void {
    const fineNowCharge = selections['FineNow'];
    if (fineNowCharge) {
      this.pcnDetails.selectedCharge = fineNowCharge;
      this.pcnDetails.selectedChargeId = fineNowCharge.id;
      this.pcnDetails.fineNow = fineNowCharge.totalAmount;
    }

    this.pendingConfirmations = [];
    this.global.pcnPrepopulateLoading = true;

    this.pcnBlockService.processInterfacedPcn(this.pcnDetails).subscribe(
      (result) => {
        if (result.success) {
          this.applySuccessfulPcn(result.pcn);
        } else {
          this.global.pcnPrepopulateLoading = false;
          if (result.pcn && result.requiredConfirmations?.length) {
            // Still unresolved (e.g. a race, or a second field) - show the modal again.
            this.showConfirmationsRequired(result.pcn, result.requiredConfirmations);
          } else {
            this.globalObs.pushError.next({
              status: 200,
              statusText: 'Success',
              message: result.errorMessage,
              url: '',
              date: new Date(),
            });
          }
        }
      },
      (error) => {
        this.global.pcnPrepopulateLoading = false;
        this.globalObs.pushError.next({
          status: 200,
          statusText: 'Success',
          message: 'Could not set PCN',
          url: '',
          date: new Date(),
        });
      }
    );
  }

  // Called by pcn-confirmation-modal (via welcome-layout.component.html) when the user closes the
  // modal because at least one confirmation has no selectable options - a genuine configuration gap
  // they cannot resolve from here. There is nothing to save, so this hands off to the same full
  // reset used elsewhere (resetPcn()) to return the user to a blank /pcn-details screen, rather than
  // leaving them on a half-populated form for a PCN that was never actually saved. Clearing
  // pendingConfirmations/pcnPrepopulateLoading first is what actually closes the modal itself;
  // resetPcn() then navigates away and blanks the rest of the state.
  onConfirmationCancelled(): void {
    this.pendingConfirmations = [];
    this.global.pcnPrepopulateLoading = false;
    this.resetPcn();
  }

  tokenDataCheck() {
    if (!this.global.isTokenPcnActive) {
      // welcome-layout.component.ts's tokenCheck() can set global.pcnPrepopulateLoading true again
      // on a later, unrelated visit to /pcn-details for as long as global.isTokenValid remains true
      // from an earlier run - nothing else would catch that here, since nothing is going to be
      // processed as a result of this component instance.
      this.global.pcnPrepopulateLoading = false;
      return;
    }

    if (
      this.global.isTokenValid &&
      this.global.tokenPcn.pcn !== '' &&
      !this.isProcessingInterface
    ) {
      this.isProcessingInterface = true;
      this.isInterfacedPcn = true;
      this.global.isTokenPcnActive = false;

      // set pcn details
      this.pcnDetails = this.global.tokenPcn;
      this.pcnDetails.isInterfaced = true;

      // adjust dates to utc
      this.setDatesToUtc();

      // process the pcn
      this.pcnBlockService.processInterfacedPcn(this.pcnDetails).subscribe(
        (result) => {
          if (result.success) {
            this.applySuccessfulPcn(result.pcn);
          } else if (result.pcn && result.requiredConfirmations?.length) {
            this.isProcessingInterface = false;
            this.showConfirmationsRequired(result.pcn, result.requiredConfirmations);
          } else {
            this.isProcessingInterface = false;
            this.global.pcnPrepopulateLoading = false;
            this.globalObs.pushError.next({
              status: 200,
              statusText: 'Success',
              message: result.errorMessage,
              url: '',
              date: new Date(),
            });
          }
        },
        (error) => {
          this.isProcessingInterface = false;
          this.global.pcnPrepopulateLoading = false;
          this.globalObs.pushError.next({
            status: 200,
            statusText: 'Success',
            message: 'Could not set PCN',
            url: '',
            date: new Date(),
          });
        }
      );
    } else {
      // isTokenPcnActive was true (so tokenCheck() may have set pcnPrepopulateLoading true) but
      // there is nothing valid to actually process (e.g. an incomplete tokenPcn with no pcn
      // number, or this is already mid-flight) - processInterfacedPcn is never reached on this
      // path, so nothing else would ever reset the flag.
      this.global.pcnPrepopulateLoading = false;
    }
  }
  loadFromPayload(payload: any): void {
    this.resetPcn();
    // resetPcn() just cleared this, so it must be set true again here (after, not before, the
    // resetPcn() call above) rather than by the caller - this method owns the flag's full
    // lifecycle for this operation and resets it again itself on every exit path below.
    this.global.pcnPrepopulateLoading = true;
    this.isApplyingPayload = true;

    const pcn = (payload?.pcn || '').trim();
    if (!pcn || pcn === 'undefined') {
      this.pcnNumberValidation = false;
      this.isApplyingPayload = false;
      // No pcn number to process - processInterfacedPcn is never reached on this path, so nothing
      // below would ever clear this otherwise.
      this.global.pcnPrepopulateLoading = false;
      return;
    }

    const pcnModel = new PcnModel();
    pcnModel.pcn = pcn;
    pcnModel.contravention = payload.contravention || '';
    pcnModel.selectedSourceId = payload.selectedSourceId ?? 1;
    pcnModel.selectedStageId = payload.selectedStageId ?? 1;
    pcnModel.selectedActionId = payload.selectedActionId ?? 1;
    pcnModel.selectedChargeId =
      payload.selectedChargeId && payload.selectedChargeId > 0 ? payload.selectedChargeId : 0;
    try {
      pcnModel.paidAmount = new Decimal(payload.paid || payload.paidAmount || 0);
    } catch {
      pcnModel.paidAmount = new Decimal(0);
    }
    try {
      pcnModel.fineNow = new Decimal(payload.fine || payload.fineNow || 0);
    } catch {
      pcnModel.fineNow = new Decimal(0);
    }
    pcnModel.isInterfaced = true;

    if (payload.issued && moment(payload.issued).isValid()) {
      const d = moment(payload.issued);
      pcnModel.dateIssued = new Date(
        Date.UTC(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second())
      );
    }
    if (payload.paymentDate && moment(payload.paymentDate).isValid()) {
      const d = moment(payload.paymentDate);
      pcnModel.paymentDate = new Date(Date.UTC(d.year(), d.month(), d.date()));
    }
    if (payload.letterDate) {
      pcnModel.letterDate = new Date(payload.letterDate);
    }

    this.pdfUrl = payload.pdf || '';
    if (this.pdfUrl) {
      (pcnModel as any).pdfUrl = this.pdfUrl;
      localStorage.setItem('payloadPdfUrl', this.pdfUrl);
    }

    this.pcnDetails = pcnModel;

    this.pcnBlockService.processInterfacedPcn(this.pcnDetails).subscribe({
      next: (result) => {
        if (result.success) {
          this.applySuccessfulPcn(result.pcn);
        } else if (result.pcn && result.requiredConfirmations?.length) {
          this.isApplyingPayload = false;
          this.showConfirmationsRequired(result.pcn, result.requiredConfirmations);
        } else {
          this.isApplyingPayload = false;
          this.global.pcnPrepopulateLoading = false;
          this.globalObs.pushError.next({
            status: 200,
            statusText: 'Success',
            message: result.errorMessage,
            url: '',
            date: new Date(),
          });
        }
      },
      error: () => {
        this.isApplyingPayload = false;
        this.global.pcnPrepopulateLoading = false;
        this.globalObs.pushError.next({
          status: 200,
          statusText: 'Success',
          message: 'Could not process PCN from payload',
          url: '',
          date: new Date(),
        });
      },
    });
  }

  triggerFormSubmit(): void {
    const maxAttempts = 5;
    let attempts = 0;

    if (this.payloadSubmitTimer) {
      window.clearInterval(this.payloadSubmitTimer);
    }

    this.payloadSubmitTimer = window.setInterval(() => {
      const btn = document.getElementById('btnContinue') as HTMLButtonElement | null;

      if (btn && !btn.disabled && this.pcnDetails.pcn?.trim()) {
        btn.click();

        if (this.payloadSubmitTimer) {
          window.clearInterval(this.payloadSubmitTimer);
          this.payloadSubmitTimer = undefined;
        }

        return;
      }

      attempts++;

      if (attempts >= maxAttempts && this.payloadSubmitTimer) {
        window.clearInterval(this.payloadSubmitTimer);
        this.payloadSubmitTimer = undefined;
      }
    }, 600);
  }

  setIsAdminAccount() {
    const user = this.authService.getCurrentUser();
    this.isAdminAccount = user?.adminAccess === true && user?.username?.includes('admin@');
  }

  setPcnDefaultValue() {
    this.pcnNumberClass = '';
    this.contraventionClass = '';
    this.interactPcn = false;
    this.pcnNumberValidation = false;
    this.isActionPcn = false;
    this.pcnDetails = new PcnModel();
  }

  setPcnStorageValue() {
    if (
      this.global.PCN !== null &&
      this.global.PCN.pcn !== undefined &&
      this.global.PCN.pcn !== ''
    ) {
      this.interactPcn = true;
      this.pcnNumberValidation = true;
      this.isActionPcn = true;
      this.pcnDetails = this.global.PCN;
      this.dateIssued = new Date(this.pcnDetails.dateIssued);
      this.paymentDate = new Date(this.pcnDetails.paymentDate);
      this.letterDate = new Date(this.pcnDetails.letterDate);
      this.setIsActionPcn(this.pcnDetails.selectedActionId);
    }
  }

  getPcnPrefix() {
    this.pcnBlockService.getDefaultPcnPrefix().subscribe({
      next: (result) => {
        this.pcnPrefix = result;
      },
      error: (e) => {
        this.pcnPrefix = 'BL';
      },
    });
  }

  submitPcn(event) {
    event.preventDefault();
    this.removeNexusStorage();
    // removeNexusStorage drops this PCN's session guid, so a new audit session starts here; the
    // touched questions belong to the old one.
    this.global.touchedQuestionIds.clear();
    if (
      !this.pcnNumberValidation /* !this.contraventionValidation ||*/ ||
      /* this.pcnDetails.contravention === '' ||*/
      this.tempContravention === '' ||
      !this.isFineNowValid()
    ) {
      return;
    }

    // get fine amount value
    const el = <HTMLSelectElement>document.getElementById('fineAmountId');
    if (!isNaN(parseInt(el.value, 10))) {
      this.pcnDetails.fineNow = new Decimal(el.value); // parseInt(this.pcnDetails.fineAmounts.find(x => parseInt(x.id) === amountId));
    }
    this.pcnDetails.dateIssued = moment(this.dateIssued).utc().toDate();
    this.pcnDetails.paymentDate = moment(this.paymentDate).utc().toDate();
    // this.pcnDetails.paidAmount = new Decimal(
    //   this.pcnDetails.paidAmount.toString() === '' ? '0' : this.pcnDetails.paidAmount.toString()
    // );

    // save pcn logic
    this.pcnBlockService.checkPcnIsAvailable(this.pcnDetails.pcn).subscribe((result) => {
      const pcnExists = result;
      this.savePcn(pcnExists);
    });
  }

  saveInterfacePcn(pcnExists) {
    if (this.savingStatus) {
      return;
    }
    this.setDatesToUtc();
    this.savingStatus = true;
    this.pcnBlockService.setPcn(this.pcnDetails.pcn, this.pcnDetails, pcnExists).subscribe({
      next: (result) => {
        if (result.success) {
          this.globalObs.togglePcnPanel.next('close');
          this.pcnBlockService.setLocalPcn(this.pcnDetails);
          this.global.PCN = this.pcnDetails;
          this.globalObs.onPcnChanged.next(this.pcnDetails);
          this.restorePanelState();
          this.resetReasons();
          this.router.navigate(['/reasons']);
        } else {
          this.globalObs.pushError.next({
            status: 200,
            statusText: 'Success',
            message: result.errorMessage,
            url: '',
            date: new Date(),
          });
        }
        this.isServerPcnError = result.success;
        this.serverPcnErrorMessage = result.errorMessage;
      },
      error: (err) => {},
      complete: () => {
        this.savingStatus = false;
      },
    });
  }

  savePcn(pcnExists) {
    if (this.savingStatus) {
      return;
    }

    if (
      !this.pcnNumberValidation /* !this.contraventionValidation ||*/ ||
      /* this.pcnDetails.contravention === '' ||*/
      this.tempContravention === '' ||
      !this.isFineNowValid() ||
      this.waitingFineAmount
    ) {
      return;
    }

    this.setDatesToUtc();

    this.savingStatus = true;

    /* Update PCN Details from Temp values */
    this.pcnDetails.contravention = this.tempContravention;
    this.pcnDetails.selectedStageId = this.tempStageId;
    this.pcnDetails.selectedActionId = this.tempActionId;
    this.pcnDetails.selectedSourceId = this.tempSourceId;
    this.pcnDetails.paidAmount = new Decimal(this.tempPaidAmount);
    this.pcnDetails.selectedChargeId = this.tempChargeId;

    this.pcnBlockService.setPcn(this.pcnDetails.pcn, this.pcnDetails, pcnExists).subscribe({
      next: (result) => {
        // console.log("setPcn returned");
        if (result.success) {
          this.globalObs.togglePcnPanel.next('close');
          this.pcnBlockService.setLocalPcn(this.pcnDetails);
          this.global.PCN = this.pcnDetails;
          this.global.isPcnProcessing = true;
          this.globalObs.onPcnChanged.next(this.pcnDetails);
          // DC: we dont need this? the above event triggers in Reasons which handles the reset this.
          // resetReasons();

          this.formChanged = false; // Disable button after saving
          this.restorePanelState();

          this.router.navigate(['/reasons']);
        } else {
          this.globalObs.pushError.next({
            status: 200,
            statusText: 'Success',
            message: result.errorMessage,
            url: '',
            date: new Date(),
          });
        }
      },
      error: (e) => {},
      complete: () => {
        this.savingStatus = false;
        this.snapshotFormState(); // Save the state of the pcn
      },
    });
  }

  setDatesToUtc() {
    const dateIssued = this.pcnDetails.dateIssued;
    if (moment(dateIssued).isValid()) {
      const year = moment(dateIssued).year();
      const month = moment(dateIssued).month();
      const date = moment(dateIssued).date();
      const hour = moment(dateIssued).hour();
      const minute = moment(dateIssued).minute();
      const second = moment(dateIssued).second();
      const utcDate = new Date(Date.UTC(year, month, date, hour, minute, second));
      this.pcnDetails.dateIssued = utcDate;
    }

    const paymentDate = this.pcnDetails.paymentDate;
    if (moment(paymentDate).isValid()) {
      const year = moment(paymentDate).year();
      const month = moment(paymentDate).month();
      const date = moment(paymentDate).date();
      const utcDate = new Date(Date.UTC(year, month, date));
      this.pcnDetails.paymentDate = utcDate;
    }
  }

  resetReasons() {
    // this.global.REASONS = [];
    // this.globalObs.changeREASONS.next([]);
    // this.reasonsService.setLocalReasons([]);

    // reset the selected reasons
    this.global.resetSelectedReasons();

    // fire the event to notify that selected reasons have changed
    this.globalObs.onReasonsChanged.next([]);
  }

  removeNexusStorage(pcn?: string) {
    // All of nexus-upload's per-case keys are PCN-scoped (e.g. `evidenceDocuments_<pcn>`). localStorage
    // is shared across every tab of the origin, while the "current PCN" pointer is per-tab
    // (sessionStorage) - so clearing by prefix across ALL pcns would wipe another tab's in-progress
    // case. Only clear the keys for the specific case being submitted/reset.
    const targetPcn = pcn ?? this.global?.PCN?.pcn;
    if (!targetPcn) {
      return;
    }

    const nexusKeyPrefixes = [
      'nexus_uploaded_meta_',
      'nexus_uploaded_names_',
      'evidenceDocuments_',
      'evidenceDocumentGuesses_',
      'evidenceDocumentConfidences_',
      'sessionGuid_',
      'nexus_summary_',
      'nexus_evidence_',
      'nexus_free_text_',
      'nexus_link_',
    ];

    nexusKeyPrefixes.forEach((prefix) => localStorage.removeItem(`${prefix}${targetPcn}`));
  }

  onChangePcnNumber() {
    const pcn = this.pcnDetails?.pcn?.trim();

    if (!pcn || pcn === 'undefined') {
      this.pcnNumberValidation = false;
      this.pcnNumberClass = 'error';
      return;
    }

    this.pcnNumberClass = '';
    this.pcnBlockService.getPcn(this.pcnDetails.pcn).subscribe({
      next: (result) => {
        this.interactPcn = true;

        // console.log("pcn validation res: ", result);

        // guard on success + a non-null pcn: the backend can return success:false with no
        // pcn payload (e.g. a stored PCN with data it can't resolve), and blindly hydrating
        // the form from a null pcn silently breaks the screen instead of surfacing an error
        if (result && result.success && result.pcn) {
          this.pcnNumberClass = 'success';
          this.pcnNumberValidation = true;
          this.pcnDetails = result.pcn;
          if (!this.isApplyingPayload) {
            this.tempContravention = this.pcnDetails.contravention;
            this.tempStageId = this.pcnDetails.selectedStageId;
            this.tempActionId = this.pcnDetails.selectedActionId;
            this.tempSourceId = this.pcnDetails.selectedSourceId;
            this.tempPaidAmount = new Decimal(this.pcnDetails.paidAmount);
            this.tempChargeId = this.pcnDetails.selectedChargeId;
          }

          this.isExistingPcn = this.pcnDetails.contravention !== '';

          this.setDefaultForEmptyPcnValues();

          this.dateIssued =
            this.pcnDetails.dateIssued !== null
              ? moment(this.pcnDetails.dateIssued).toDate()
              : null;
          this.paymentDate =
            this.pcnDetails.paymentDate !== null
              ? moment(this.pcnDetails.paymentDate).toDate()
              : null;
          // this.contraventionValidation = true;

          this.tooltipPcn.close();

          // this.setIsActionPcn(this.pcnDetails.selectedActionId);
          this.setIsActionPcn(this.tempActionId);
          if (!this.isApplyingPayload) {
            this.setStages();
          }

          // enable the contravention field
          this.isContraventionEnabled = true;
          // if we are loading a pcn that already exists we should set the contravention to be valid as well
          if (
            /* this.pcnDetails.contravention*/ this.tempContravention !== null &&
            this.tempContravention !== ''
          ) {
            this.isContraventionValid = true;
          }

          // focus on contravention field if pcn valid
          setTimeout(() => {
            this.contraventionField['input'].nativeElement.focus();
          }, 50);

          // this.isChargeNowEnabled = false;

          this.pcnLocked = true; // ✅ Lock the PCN field after validation
          this.snapshotFormState(); // Save the state of the pcn
        } else if (result && !result.success) {
          this.pcnLocked = false;
          this.pcnNumberValidation = false;
          this.pcnNumberClass = 'error';
          this.tooltipPcn.open();
        } else {
          this.pcnLocked = false; // Keep it unlocked if invalid
          this.pcnPrefix = this.pcnDetails.pcn.substr(0, 2).toUpperCase();
          this.pcnNumberValidation = false;
          this.pcnNumberClass = 'error';

          this.isPcnPrefixError = true;
          // this.isServerPcnError = false;
          this.tooltipPcn.open();
        }

        /* DC: the new "result models" will contain a success and error message variables which we can use to better control the error messages
              that are presented to the users directly from the back-end code*/
        this.isServerPcnError = !result.success;
        this.serverPcnErrorMessage = result.errorMessage;

        this.trackFormChanges();
      },
      error: (error) => {
        // console.log({ error });
        this.pcnLocked = false; // Keep it unlocked if invalid
        this.pcnPrefix = this.pcnDetails.pcn.substr(0, 2).toUpperCase();
        this.interactPcn = true;
        this.pcnNumberValidation = false;
        this.pcnNumberClass = 'error';

        this.isUnhandledError = true;
        this.tooltipPcn.open();
      },
    });
  }

  getPcnNumberError() {
    if (this.isUnhandledError) {
      return `An unhandled error has occurred, please try again`;
    } else if (this.isPcnPrefixError) {
      return this.pcnPrefix + ` is not a valid prefix`;
    } else {
      return this.isServerPcnError ? this.serverPcnErrorMessage : '';
    }
  }

  snapshotFormState() {
    this.initialState = {
      contravention: this.pcnDetails.contravention,
      selectedActionId: this.pcnDetails.selectedActionId,
      selectedStageId: this.pcnDetails.selectedStageId,
      selectedChargeId: this.pcnDetails.selectedCharge?.id,
      paidAmount: this.pcnDetails.paidAmount,
      fineNow: this.pcnDetails.selectedCharge?.totalAmount,
      selectedSourceId: this.pcnDetails.selectedSourceId,
    };
  }

  // Track if form has changed
  trackFormChanges() {
    if (!this.initialState) {
      // console.warn('[DEBUG] Initial state is undefined — assuming form has changed ✅');
      this.formChanged = true;
      this.hasPendingChanges = true;
      this.forcePinPanel();
      this.globalObs.onPcnChanging.next(null);
      return;
    }

    // console.log('[DEBUG] Initial State:', this.initialState);
    // console.log('[DEBUG] Current PCN Details:', this.pcnDetails);
    this.formChanged =
      // this.pcnDetails.contravention !== this.initialState.contravention ||
      this.tempContravention !== this.initialState.contravention ||
      // this.pcnDetails.selectedActionId !== this.initialState.selectedActionId ||
      this.tempActionId !== this.initialState.selectedActionId ||
      // this.pcnDetails.selectedStageId !== this.initialState.selectedStageId ||
      this.tempStageId !== this.initialState.selectedStageId ||
      // this.pcnDetails.selectedChargeId !== this.initialState.selectedChargeId ||
      this.tempChargeId !== this.initialState.selectedChargeId ||
      // this.pcnDetails.paidAmount !== this.initialState.paidAmount ||
      // !this.pcnDetails.paidAmount.equals(this.initialState.paidAmount) ||
      !this.tempPaidAmount.equals(this.initialState.paidAmount) ||
      // this.pcnDetails.selectedSourceId !== this.initialState.selectedSourceId;
      this.tempSourceId !== this.initialState.selectedSourceId;

    if (!this.formChanged && this.isExistingPcn) {
      this.formChanged = true;
    } // Always set to true if PCN already exists

    // Update pending changes state and force pin if there are changes
    const isEditMode = this.submitButtonValue === 'Update';
    this.hasPendingChanges = this.formChanged && isEditMode;

    if (this.hasPendingChanges) {
      this.forcePinPanel();
    }

    if (this.formChanged) {
      this.globalObs.onPcnChanging.next(null);
    }
    // This code will determine if the continue button will
    // be disabled or not and then report it to the console

    // console.log('-'.repeat(50));
    const conditions = {
      '!formChanged': !this.formChanged,
      savingStatus: this.savingStatus,
      '!pcnNumberValidation': !this.pcnNumberValidation,
      '!isContraventionValid': !this.isContraventionValid,
      'contravention === ""': this.tempContravention === '',
      '!isFineNowValid()': !this.isFineNowValid(),
      waitingFineAmount: this.waitingFineAmount,
      isActionChanging: this.isActionChanging,
    };

    const failingConditions = Object.entries(conditions)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    const isDisabled = failingConditions.length > 0;

    // console.log(
    //   `%c[DEBUG] Continue button is ${isDisabled ? 'DISABLED ❌' : 'ENABLED ✅'}`,
    //   `color: ${isDisabled ? '#f44336' : '#4CAF50'}; font-weight: bold; font-size: 14px`
    // );

    if (failingConditions.length > 0) {
      // console.log('%cCaused by:', 'color: #f44336; font-weight: bold;');
      // failingConditions.forEach((condition) => console.log(`⛔ %c${condition}`, 'color: #f44336'));
    } else {
      // console.log('%cAll conditions passed ✅', 'color: #4CAF50');
    }
  }

  setStages() {
    // console.log("Setting Stages...");
    this.isStagesLoading = true;
    // after received the response get stages
    this.pcnBlockService
      .getStages(
        this.tempContravention,
        this.tempSourceId,
        this.tempStageId,
        this.pcnDetails.fineNow
      )
      .subscribe({
        next: (stages) => {
          this.pcnDetails.stages = stages;

          const exists = stages.some((s) => s.id === this.tempStageId);

          if (!exists) {
            this.tempStageId = stages[0]?.id;
          }

          this.onChangeStage(this.tempStageId);
          this.isStagesLoading = false;
        },
        error: (error) => {
          this.isStagesLoading = false;
        },
      });
  }

  setDefaultForEmptyPcnValues() {
    // if (this.pcnDetails.selectedActionId === undefined) {
    //   this.pcnDetails.selectedActionId = parseInt(this.pcnDetails.actions[0].id, 10);
    // }
    if (this.tempActionId === undefined) {
      this.tempActionId = parseInt(this.pcnDetails.actions[0].id, 10);
    }

    // if (this.pcnDetails.selectedStageId === undefined) {
    //   this.pcnDetails.selectedStageId = this.pcnDetails.stages[0].id;
    //   this.pcnDetails.reOfferDiscount = true;
    // }
    if (this.tempStageId === undefined) {
      this.tempStageId = this.pcnDetails.stages[0].id;
      this.pcnDetails.reOfferDiscount = true;
    }
    // if (this.pcnDetails.selectedSourceId === undefined) {
    //   this.pcnDetails.selectedSourceId = parseInt(this.pcnDetails.sources[0].id, 10);
    // }
    if (this.tempSourceId === undefined) {
      this.tempSourceId = parseInt(this.pcnDetails.sources[0].id, 10);
    }
  }

  generatePCN(): void {
    this.pcnBlockService.generatePcn(this.pcnPrefix).subscribe({
      next: (response) => {
        this.pcnDetails.pcn = response.pcn; // Update the PCN in the UI
        this.onChangePcnNumber();
        // console.log('Generated PCN:', response);
      },
      error: (error) => {
        console.error('Error generating PCN:', error);
      },
    });
  }

  // onChangeContravention(contravention: string) {
  onChangeContraventionDebounced(contravention: string) {
    // console.log('Contravention Changed:', contravention);
    // this.trackFormChanges();

    // console.log("Contravention Changing...");
    this.waitingFineAmount = true;

    // set fine amount to default
    this.pcnDetails.fineNow = new Decimal(0);

    // set the selected charge to default
    // this.pcnDetails.selectedChargeId = 0;
    this.tempChargeId = 0;

    // console.log("contra: ", contravention);

    // validate the contravention
    this.pcnBlockService.validateContravention(contravention).subscribe({
      next: (result) => {
        // console.log("contraValResult: ", result);

        if (result.success) {
          this.isContraventionValid = true;
          this.contraventionClass = 'success';
          this.pcnDetails.contraventionSuffix = result.suffix;
          this.pcnBlockService.checkPcnIsAvailable(this.pcnDetails.pcn).subscribe({
            next: (res) => {
              if (res) {
                this.saveContravention(contravention);
              }

              if (!this.isApplyingPayload) {
                if (
                  this.pcnDetails.contraventionSuffix.toLocaleLowerCase().includes('j') ||
                  this.cctvContraventions.indexOf(parseInt(contravention, 10)) > -1
                ) {
                  this.tempSourceId = 2;
                } else {
                  this.tempSourceId = 1;
                }
              }

              this.tooltipContravention.close();
              this.setStages();
            },
            error: (error) => {
              // console.log(`Contravention invalid: `, error);
            },
            complete: () => {
              this.waitingFineAmount = false;
              this.trackFormChanges();
            },
          });
        } else {
          this.isChargeNowEnabled = false;
          this.contraventionClass = 'error';
          this.isContraventionValid = false;
          this.tooltipContravention.open();
          this.waitingFineAmount = false;
        }
      },
      error: (error) => {
        this.isContraventionValid = false;
        this.isChargeNowEnabled = false;
      },
      complete: () => {
        this.trackFormChanges();
      },
    });
  }

  saveContravention(contravention: string) {
    this.pcnBlockService
      .saveNewContravention(this.pcnDetails.pcn, parseInt(contravention, 10))
      .subscribe({
        next: (result) => {
          if (result) {
            this.setStages();
            this.contraventionClass = 'success';
            this.tooltipContravention.close();
            this.snapshotFormState(); // Update baseline only on success
          } else {
            this.contraventionClass = 'error';
            this.tooltipContravention.open();
          }
        },
        error: () => {
          this.contraventionClass = 'error';
          this.tooltipContravention.open();
        },
        complete: () => {
          this.trackFormChanges(); // Still track changes regardless of outcome
        },
      });
  }

  onChangeAction(action) {
    this.trackFormChanges();
    this.isActionChanging = true;

    this.setIsActionPcn(action);

    // check if this is not an action pcn (Removed or Clamped)
    if (!this.isActionPcn) {
      // Use optional chaining and default values for safety
      const firstSourceId = this.pcnDetails.sources?.[0]?.id;
      if (firstSourceId) {
        // this.pcnDetails.selectedSourceId = parseInt(firstSourceId, 10);
        this.tempSourceId = parseInt(firstSourceId, 10);
      }

      this.setStages();

      const defaultStage = this.pcnDetails.stages?.[2];
      if (defaultStage) {
        // this.pcnDetails.selectedStageId = defaultStage.id;
        this.tempStageId = defaultStage.id;
        this.onChangeStage(defaultStage.id);
      }
      // // Always calculate fine + mark action done after stage updates
      // const tempCharge = this.pcnDetails.fineAmounts?.find(amount => amount.id === this.tempChargeId);
      // console.log('tempCharge:', tempCharge);
      // //this.pcnDetails.fineNow = tempCharge?.totalAmount;
      // // this.pcnDetails.paidAmount = new Decimal(this.pcnDetails.fineNow);
      // this.tempPaidAmount = new Decimal(this.pcnDetails.fineNow);
    }

    this.onChangeStage(this.tempStageId); // Update stage based on action change

    // Always clear the changing flag
    this.isActionChanging = false;
  }

  setIsActionPcn(action) {
    this.isActionPcn = action === 1; /* pcn only */
  }

  onChangeSource(source) {
    // we need to set the pcn details records to the updated source
    // this.pcnDetails.selectedSourceId = source;
    this.tempSourceId = source;
    this.setStages();
  }

  getContraventionError() {
    return `contravention is not valid`;
  }

  /**
   * Gets the charge amounts to be displayed in Charge Now
   */
  updateChargeNow() {
    if (!this.isContraventionValid) {
      this.getContraventionError();
      return;
    }

    this.isChargeNowEnabled = false;

    if (!this.isInterfacedPcn) {
      this.pcnDetails.fineNow = new Decimal(0);
    }

    this.pcnBlockService
      .getChargeNowBySelectedStage(this.tempStageId, this.tempContravention)
      .subscribe({
        next: (result) => {
          if (result) {
            this.pcnDetails.fineAmounts = result.amounts;

            if (!this.isInterfacedPcn) {
              const payloadCharge = this.pcnDetails.fineAmounts.find(
                (amount) => amount.id === this.tempChargeId
              );

              if (payloadCharge) {
                this.pcnDetails.fineNow = payloadCharge.totalAmount;
                this.pcnDetails.selectedCharge = payloadCharge;
              } else if (result.default && result.default.totalAmount !== 0) {
                const defaultCharge = this.pcnDetails.fineAmounts.find(
                  (amount) => amount.id === result.default.id
                );

                if (defaultCharge) {
                  this.pcnDetails.fineNow = defaultCharge.totalAmount;
                  this.tempChargeId = defaultCharge.id;
                  this.pcnDetails.selectedCharge = defaultCharge;
                } else {
                  const firstCharge = this.pcnDetails.fineAmounts[0];

                  if (firstCharge) {
                    this.pcnDetails.fineNow = firstCharge.totalAmount;
                    this.tempChargeId = firstCharge.id;
                    this.pcnDetails.selectedCharge = firstCharge;
                  }
                }
              } else {
                const firstCharge = this.pcnDetails.fineAmounts[0];

                if (firstCharge) {
                  this.pcnDetails.fineNow = firstCharge.totalAmount;
                  this.tempChargeId = firstCharge.id;
                  this.pcnDetails.selectedCharge = firstCharge;
                }
              }
            }

            this.isChargeNowEnabled = true;

            if (this.pcnDetails.selectedCharge) {
              this.onFineNowChange();
            }

            this.snapshotFormState();
          } else {
            this.globalObs.pushError.next({
              status: 200,
              statusText: 'Success',
              message: 'Charge now list is empty',
              url: '',
              date: new Date(),
            });

            this.isChargeNowEnabled = false;
          }
        },
        error: () => {
          this.isChargeNowEnabled = false;
        },
        complete: () => {
          if (this.tempActionId !== 1) {
            this.tempPaidAmount = new Decimal(this.pcnDetails.fineNow);
          } else {
            this.tempPaidAmount = new Decimal(0);
          }

          const wasApplyingPayload = this.isApplyingPayload;

          this.isApplyingPayload = false;
          this.trackFormChanges();

          if (
            wasApplyingPayload &&
            this.pcnDetails.pcn?.trim() &&
            this.pcnNumberValidation &&
            this.isContraventionValid
          ) {
            this.triggerFormSubmit();
          }
        },
      });
  }

  /**
   * This method is used to update the selected stage and is called as a result of a requested (user or code) change of stage
   */
  onChangeStage(stage) {
    this.trackFormChanges();
    // if the passed stage is null then we should return
    if (stage === undefined) {
      return;
    }

    // find the stage object for the passed id
    const stageObject = this.pcnDetails.stages.filter((a) => a.id === stage)[0];

    // check if a stage has been found for the passed id
    if (stageObject === undefined) {
      // if the selected stage is not found in the list of stages this is most likely because
      // a change of contravention or action has occured which has updated the list of stages
      // we can simply set the selected stage id to the first in the list

      // this.pcnDetails.selectedStageId = this.pcnDetails.stages[0].id;
      this.tempStageId = this.pcnDetails.stages[0].id;
    } else {
      // set the selected staged id to the stage that has been passed
      // this.pcnDetails.selectedStageId = stageObject.id;
      this.tempStageId = stageObject.id;

      // determine whether we should set the re-offer discount variable against the pcn
      this.pcnDetails.reOfferDiscount = stageObject.isDiscount;
    }

    // const stageObjDescription = stageObject.description.indexOf('discount') !== -1 ? 'discount' : stageObject.description;
    // const chargeNowContravention = this.pcnDetails.contravention !== '' ?
    //   parseInt(this.pcnDetails.contravention) : 0;

    // send the selected stage to the backend
    // this.pcnBlockService.setSelectedStage(stageObject.id);

    this.updateChargeNow();
  }

  /**
   * This function determines if the fine now value is valid
   * @returns if the fine now value is valid
   */
  isFineNowValid() {
    // return this.pcnDetails.selectedChargeId !== null && this.pcnDetails.selectedChargeId > 0;
    return this.tempChargeId !== null && this.tempChargeId > 0;
  }

  /**
   * Event called when the Current Charge value is changed
   */
  onFineNowChange() {
    this.pcnDetails.selectedCharge = this.pcnDetails.fineAmounts.find(
      (f) => f.id === this.tempChargeId
    );

    if (!this.pcnDetails.selectedCharge) {
      return;
    }

    this.pcnDetails.fineNow = this.pcnDetails.selectedCharge.totalAmount;

    this.pcnDetails.selectedFineAmount = new Decimal(this.pcnDetails.fineNow).minus(
      this.tempPaidAmount
    );

    this.trackFormChanges();
  }
  // onFineNowChange() {
  //   // Ensure tempPaidAmount is always a Decimal
  //   if (!(this.tempPaidAmount instanceof Decimal)) {
  //     this.tempPaidAmount = new Decimal(this.tempPaidAmount ?? 0);
  //   }

  //   if (this.tempPaidAmount.isNegative()) {
  //     this.tempPaidAmount = new Decimal(0);
  //   }

  //   // Find the selected charge
  //   this.pcnDetails.selectedCharge = this.pcnDetails.fineAmounts.find(
  //     (f) => f.id === this.pcnDetails.selectedChargeId
  //   );

  //   // Update fine amount and difference
  //   this.pcnDetails.fineNow = this.pcnDetails.selectedCharge.totalAmount;

  //   this.pcnDetails.selectedFineAmount = new Decimal(this.pcnDetails.fineNow).minus(
  //     this.tempPaidAmount
  //   );

  //   this.trackFormChanges();
  // }
  // onFineNowChange() {
  //   // correct paid value if 'empty'
  //   // if (
  //   //   this.pcnDetails.paidAmount === undefined ||
  //   //   this.pcnDetails.paidAmount === null ||
  //   //   this.pcnDetails.paidAmount < new Decimal(0)
  //   // ) {
  //   //   this.pcnDetails.paidAmount = new Decimal(0);
  //   // }
  //   if (this.tempPaidAmount === undefined ||
  //     this.tempPaidAmount === null ||
  //     this.tempPaidAmount < new Decimal(0)) {
  //     this.tempPaidAmount = new Decimal(0);
  //   }

  //   // set variables based on selected charge id
  //   this.pcnDetails.selectedCharge = this.pcnDetails.fineAmounts.find(
  //     (f) => f.id === this.pcnDetails.selectedChargeId
  //   );
  //   this.pcnDetails.fineNow = this.pcnDetails.selectedCharge.totalAmount;
  //   // this.pcnDetails.selectedFineAmount = new Decimal(this.pcnDetails.fineNow).minus(
  //   //   this.pcnDetails.paidAmount
  //   // );
  //   this.pcnDetails.selectedFineAmount = new Decimal(this.pcnDetails.fineNow).minus(
  //     this.tempPaidAmount
  //   );

  //   this.trackFormChanges();
  // }

  public resetPcn(): void {
    // capture before global.PCN is wiped below - removeNexusStorage needs the case being abandoned
    const pcnBeingReset = this.global?.PCN?.pcn;

    // clear token validation
    this.global.isPcnProcessing = false;
    this.global.isTokenValid = false;
    this.global.isTokenPcnActive = false;
    this.global.encryptedToken = '';
    // A full reset promises a genuinely blank /pcn-details, so this must come with the others
    // above - it's a shared singleton flag, not scoped to this page's component instance, and
    // nothing else guarantees it isn't still stuck true from an earlier interfaced PCN.
    this.global.pcnPrepopulateLoading = false;
    this.global.PCN = new PcnModel();
    this.global.questionsAndAnswers = [];
    this.global.touchedQuestionIds.clear();
    this.questionServ.setLocalQuestionsAndAnswers([]);
    this.pcnBlockService.setLocalPcn(new PcnModel());
    this.setPcnDefaultValue();
    this.resetReasons();
    this.removeNexusStorage(pcnBeingReset);
    this.globalObs.onPcnChanged.next(null);
    // set pcn close status to false
    this.pcnBlockService.setClosePcn(false);
    this.storageService.lockReasons(false);
    // this.global.lockReasons(false);
    this.global.isPcnClosed = false;
    this.global.isReasonsLocked = false;
    // disable contravention
    this.isContraventionEnabled = false;
    this.isContraventionValid = false;
    this.pcnLocked = false; // Unlock the PCN field after reset
    this.snapshotFormState();
    this.trackFormChanges();

    // reset temp values
    this.tempContravention = '';
    this.tempStageId = undefined;
    this.tempActionId = undefined;
    this.tempSourceId = undefined;
    this.tempPaidAmount = new Decimal(0);

    // trigger a pcn reset notification
    // this.globalObs.onResetPcn.next(true); //DC(20250311): we don't need this? it is only triggered (currently) by reasons and it simply navigates to the pcn details page, which we do below...
    // redirect to the pcn details component
    this.router.navigate(['/pcn-details']);
  }

  onPcnResetRequest() {
    this.resetPcn();
  }

  onQuestionsChanging() {
    this.disablePcnBlock = true;
  }
  onQuestionsChanged() {
    this.disablePcnBlock = false;
  }

  // A failed refresh has to release the block too - otherwise it stayed
  // disabled with nothing outstanding to re-enable it.
  onQuestionsFailed() {
    this.disablePcnBlock = false;
  }
  onLetterChanging() {
    this.disablePcnBlock = true;
  }
  onLetterChanged() {
    this.disablePcnBlock = false;
  }

  ngOnDestroy() {
    if (this.onPcnResetRequestEvent !== undefined) {
      this.onPcnResetRequestEvent.unsubscribe();
    }
    if (this.onQuestionsChangingEvent !== undefined) {
      this.onQuestionsChangingEvent.unsubscribe();
    }
    if (this.onQuestionsChangedEvent !== undefined) {
      this.onQuestionsChangedEvent.unsubscribe();
    }
    if (this.onQuestionsFailedEvent !== undefined) {
      this.onQuestionsFailedEvent.unsubscribe();
    }
    if (this.onLetterChangingEvent !== undefined) {
      this.onLetterChangingEvent.unsubscribe();
    }
    if (this.onLetterChangedEvent !== undefined) {
      this.onLetterChangedEvent.unsubscribe();
    }
    if (this.payloadSubmitTimer) {
      window.clearInterval(this.payloadSubmitTimer);
    }
  }

  private forcePinPanel(): void {
    // Only apply in edit mode
    if (this.submitButtonValue !== 'Update') {
      return;
    }

    // Capture the original state ONCE from storage (the actual persisted preference)
    if (this.panelStateBeforeChanges === null) {
      this.panelStateBeforeChanges = this.storageService.isPcnPanelLocked();
    }

    // Force pin visually only - DON'T write to storage
    if (!this.global.isPcnPanelLocked) {
      this.global.setPcnPanelLocked(true);
    }
  }

  private restorePanelState(): void {
    if (this.panelStateBeforeChanges !== null) {
      // Restore the visual state to match what's in storage
      this.global.setPcnPanelLocked(this.panelStateBeforeChanges);
      this.panelStateBeforeChanges = null;
    }
    this.hasPendingChanges = false;
  }

  // Shared by applySuccessfulPcn and showConfirmationsRequired below: both need pcnDetails' actions/
  // sources/stages/fineAmounts/selectedCharge/repId brought in line with what the backend resolved,
  // whether the save succeeded or was refused pending confirmation.
  private applyPcnContext(pcn: any): void {
    this.pcnDetails.actions = pcn.actions;
    this.pcnDetails.selectedActionId = pcn.selectedActionId;
    this.pcnDetails.sources = pcn.sources;
    this.pcnDetails.selectedSourceId = pcn.selectedSourceId;
    this.pcnDetails.stages = pcn.stages;
    this.pcnDetails.selectedStageId = pcn.selectedStageId;
    this.pcnDetails.fineAmounts = pcn.fineAmounts;
    this.pcnDetails.selectedCharge = pcn.selectedCharge;
    this.pcnDetails.selectedChargeId = pcn.selectedChargeId;
    this.pcnDetails.repId = pcn.repId;
  }

  // Shared by tokenDataCheck()/loadFromPayload()'s success branches AND onConfirmationsResolved()
  // above (the resubmission after the user picks a value in the confirmation modal), so there is
  // one place that knows how to move from "backend accepted this PCN" to "show it in /reasons".
  private applySuccessfulPcn(pcn: any): void {
    this.interactPcn = true;
    this.pcnNumberValidation = true;

    this.applyPcnContext(pcn);
    this.setIsActionPcn(pcn.selectedActionId);
    this.pcnDetails.selectedFineAmount = pcn.selectedCharge?.totalAmount;

    this.isProcessingInterface = false;
    this.isApplyingPayload = false;
    // global.pcnPrepopulateLoading is a shared singleton, not a field scoped to this page's own
    // component instance - it does NOT get reset just because this method navigates away. Left
    // uncleared here, it stays true for the rest of the session and hangs the NEXT time the user
    // lands back on /pcn-details for any reason (e.g. pressing Home or Cancel after a letter has
    // been generated), with nothing left to ever reset it.
    this.global.pcnPrepopulateLoading = false;

    this.snapshotFormState(); // Save the state of the pcn

    /* Set the TEMP values */
    this.tempContravention = this.pcnDetails.contravention;
    this.tempStageId = this.pcnDetails.selectedStageId;
    this.tempActionId = this.pcnDetails.selectedActionId;
    this.tempSourceId = this.pcnDetails.selectedSourceId;
    this.tempPaidAmount = new Decimal(this.pcnDetails.paidAmount);
    this.tempChargeId = this.pcnDetails.selectedChargeId;

    // redirect to reasons
    this.globalObs.togglePcnPanel.next('close');
    this.pcnBlockService.setLocalPcn(this.pcnDetails);
    this.global.isPcnProcessing = true;
    this.global.PCN = this.pcnDetails;
    this.globalObs.onPcnChanged.next(this.pcnDetails);
    this.resetReasons();
    this.router.navigate(['/reasons']);
  }

  // One or more fields (e.g. Fine Now) could not be automatically determined; the PCN was not
  // saved. Deliberately does NOT try to make the underlying pcn-block form interactive - that
  // form's state (isContraventionValid, temp* staging, formChanged tracking) is built entirely
  // around "validate, then immediately navigate away". Instead this only updates the context
  // pcnDetails needs to resubmit later, and hands the confirmation off to pcn-confirmation-modal, a
  // small self-contained component that doesn't depend on any of that form state. It also clears
  // global.pcnPrepopulateLoading - unlike the success path, which navigates away before that flag
  // would matter, this must reset it explicitly or /pcn-details is left on its loading spinner.
  private showConfirmationsRequired(pcn: any, confirmations: PcnFieldConfirmation[]): void {
    this.applyPcnContext(pcn);

    this.global.pcnPrepopulateLoading = false;
    this.pendingConfirmations = confirmations || [];
  }

  // public onClickNo() {
  //   this.fineNowChangedMessage = false;
  // }

  // public fineNowChanged() {
  //   if (this.pcnDetails.fineNowChanged === true) {
  //     this.fineNowChangedMessage = true;
  //   }
  // }
}
